import React, { useEffect } from "react";

import classNames from "classnames";
import { Controller, get, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";

import ErrorMessage from "../../form/ErrorMessage";
import FieldContainer from "../../form/FieldContainer";
import Label from "../../form/Label";
import {
  clearIndicatorStyles,
  controlStyles,
  dropdownIndicatorStyles,
  groupHeadingStyles,
  indicatorsContainerStyles,
  indicatorSeparatorStyles,
  menuPortalStyles,
  menuStyles,
  noOptionsMessageStyles,
  optionStyles,
  placeholderStyles,
  selectInputStyles,
  singleValueStyles,
  valueContainerStyles,
} from "../../form/Select";
import { wrapperClasses } from "../../form/styles";

import type { JSX } from "react";

interface SelectStatus {
  isFocused: boolean;
  isSelected: boolean;
}
type SelectClassNames = Record<
  string,
  ({ isFocused, isSelected }: SelectStatus) => string
>;

export default function TimeslotDuration(): JSX.Element {
  const {
    watch,
    setValue,
    getFieldState,
    control,
    formState: { errors },
  } = useFormContext();

  const { t } = useTranslation("editor-errors");

  const hourFieldName = "durationHours";
  const minuteFieldName = "durationMinutes";

  const isMinuteFieldDisabled = watch(hourFieldName)?.value === 24;
  const isHourFieldInvalid = getFieldState(hourFieldName)?.invalid;
  const hourFieldError = get(errors, hourFieldName);
  const isMinuteFieldInvalid = getFieldState(minuteFieldName)?.invalid;
  const minuteFieldError = get(errors, minuteFieldName);

  const getErrorMessage = ():
    | {
        message?: string;
      }
    | undefined => {
    if (isHourFieldInvalid) {
      return hourFieldError as { message?: string };
    }
    if (isMinuteFieldInvalid) {
      return minuteFieldError as { message?: string };
    }
    return undefined;
  };

  const errorMessage = getErrorMessage();

  const hourOptions = Array.from({ length: 25 }, (_, i) => ({
    value: i,
    label: `${i.toString()}h`,
  }));

  const minuteOptions = Array.from({ length: 60 }, (_, i) => ({
    value: i,
    label: `${i.toString()}m`,
  }));

  const getSelectClassNames = (isReadOnly?: boolean): SelectClassNames => ({
    control: ({ isFocused }: SelectStatus) =>
      classNames(
        (isHourFieldInvalid || isMinuteFieldInvalid) && controlStyles.isError,
        isFocused ? controlStyles.focus : controlStyles.nonFocus,
        controlStyles.base,
        isReadOnly && controlStyles.isReadOnly,
        "w-[5.625rem]!",
      ),
    input: () => selectInputStyles,
    placeholder: () => placeholderStyles,
    valueContainer: () => valueContainerStyles,
    singleValue: () => singleValueStyles,
    indicatorsContainer: () => indicatorsContainerStyles,
    clearIndicator: () => clearIndicatorStyles,
    indicatorSeparator: () => indicatorSeparatorStyles,
    dropdownIndicator: () => dropdownIndicatorStyles,
    menu: () => menuStyles,
    groupHeading: () => groupHeadingStyles,
    option: ({ isFocused, isSelected }: SelectStatus) =>
      classNames(
        isFocused && optionStyles.focus,
        isSelected && optionStyles.selected,
        optionStyles.base,
      ),
    noOptionsMessage: () => noOptionsMessageStyles,
    menuPortal: () => menuPortalStyles,
  });

  useEffect(() => {
    if (isMinuteFieldDisabled) {
      setValue(minuteFieldName, { value: 0, label: "0m" });
    }
  }, [isMinuteFieldDisabled, setValue]);

  return (
    <div className="flex">
      <FieldContainer isNoWrap>
        <Label
          isRequired
          label={t("duration", { ns: "editor-fast-channel" })}
          name="duration"
        />
        <div className={wrapperClasses({ isCompact: false })}>
          <div className="flex w-full gap-[0.3125rem]">
            <Controller
              control={control}
              name={hourFieldName}
              render={({ field }): JSX.Element => (
                <ReactSelect
                  unstyled
                  classNames={getSelectClassNames()}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  options={hourOptions}
                  placeholder="..."
                  noOptionsMessage={({
                    inputValue,
                  }: {
                    inputValue: string;
                  }): string | false => !inputValue && "no options"}
                  styles={{
                    control: () => ({}),
                  }}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                />
              )}
              rules={{
                required: t("requiredField", {
                  ns: "editor-errors",
                }),
              }}
            />
            <Controller
              control={control}
              name={minuteFieldName}
              render={({ field }): JSX.Element => (
                <ReactSelect
                  unstyled
                  classNames={getSelectClassNames(isMinuteFieldDisabled)}
                  isDisabled={isMinuteFieldDisabled}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  options={minuteOptions}
                  placeholder="..."
                  noOptionsMessage={({
                    inputValue,
                  }: {
                    inputValue: string;
                  }): string | false => !inputValue && "no options"}
                  styles={{
                    control: () => ({}),
                  }}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                />
              )}
              rules={{
                required: t("requiredField", {
                  ns: "editor-errors",
                }),
              }}
            />
          </div>
          <ErrorMessage error={errorMessage} />
        </div>
      </FieldContainer>
    </div>
  );
}
